import { Component, Inject, Input, LOCALE_ID, OnChanges, Self, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { RegisterData, RegisterParams } from '@assetaccountant/core/resolvers/register.resolver';
import { getLocaleDateString, toUniversalDateString } from '@assetaccountant/core/utils/date-utils';
import {
  TypedActivatedRoute,
  getCurrentRegisterLocale,
} from '@assetaccountant/core/utils/route-utils';
import moment from 'moment';

const FEB_28_29_MONTH_VALUE = '02-28/29';
const isEndOfFebruaryMonthDate = (monthValue: string) =>
  !!monthValue && /^02-2[89]$/.test(monthValue);

@Component({
  selector: 'app-month-year-date-selector',
  templateUrl: './month-year-date-selector.component.html',
  standalone: false,
})
export class MonthYearDateSelectorComponent implements ControlValueAccessor, OnChanges {
  @Input() dates: string[];

  dateFormat: string;

  selectedYearValue: string;
  selectedMonthValue: string;

  formattedDates: Array<{ label: string; value: string }> = [];

  disabled = false;

  private includesFebruaryEndDates = false;

  private _onChange?: (value: string | null) => void;
  private _touched: [boolean, boolean] = [false, false];
  private _onTouched?: () => void;

  ngOnChanges(changes: SimpleChanges) {
    if ('dates' in changes) {
      const dropdownDates = this.dates.map(d => this.stringDateToItem(d));

      const dateValues = Array.from(new Set(dropdownDates.map(s => s.value)));
      dateValues.sort();

      this.fixFebruaryIfEndDates(dateValues);

      this.formattedDates = dateValues.map(dateValue => {
        if (dateValue === FEB_28_29_MONTH_VALUE) {
          const febEndValue = dropdownDates.find(s => s.value === '02-28' || s.value === '02-29');

          return {
            label: febEndValue ? febEndValue.label.replace(/\d+/, '28/29') : '',
            value: dateValue,
          };
        }

        const date = dropdownDates.find(s => s.value === dateValue);
        return {
          label: date ? date.label : '',
          value: dateValue,
        };
      });

      if (this.includesFebruaryEndDates) {
        if (isEndOfFebruaryMonthDate(this.selectedMonthValue)) {
          this.selectedMonthValue = FEB_28_29_MONTH_VALUE;
        }
      } else {
        if (this.selectedMonthValue === FEB_28_29_MONTH_VALUE) {
          this.selectedMonthValue = dateValues.find(isEndOfFebruaryMonthDate) || '';
        }
      }
    }
  }

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    @Self() public control: NgControl,
    @Inject(ActivatedRoute) private route: TypedActivatedRoute<RegisterParams, RegisterData>,
  ) {
    this.control.valueAccessor = this;
    this.dateFormat = getLocaleDateString(getCurrentRegisterLocale(this.route.snapshot) || locale);
  }

  private stringDateToItem(dateAsString: string) {
    const val = moment(dateAsString);
    val.locale(this.locale);
    if (this.dateFormat === 'MDY' || this.dateFormat === 'YMD') {
      return { label: val.format('MMMM DD'), value: val.format('MM-DD') };
    } else {
      return { label: val.format('DD MMMM'), value: val.format('MM-DD') };
    }
  }

  private fixFebruaryIfEndDates(sortedDateValues: string[]) {
    const indexFeb28 = sortedDateValues.indexOf('02-28');
    const indexFeb29 = sortedDateValues.indexOf('02-29');
    /**
     * - The dates include Feb 28
     * - There are dates for every month and 2 for Feb
     * - The dates include Jan 31
     * - The date after Feb 28 is Feb 29
     */
    this.includesFebruaryEndDates =
      (indexFeb28 !== -1 || indexFeb29 !== -1) && sortedDateValues.includes('01-31');

    if (this.includesFebruaryEndDates) {
      if (indexFeb29 >= 0) {
        sortedDateValues.splice(indexFeb29, 1);
      }
      if (indexFeb28 >= 0) {
        sortedDateValues.splice(indexFeb28, 1);
      }
      sortedDateValues.splice(indexFeb28 >= 0 ? indexFeb28 : indexFeb29, 0, FEB_28_29_MONTH_VALUE);
    }
  }

  getDisplayLabel({ label, value }: { label: string; value: string }) {
    if (
      this.includesFebruaryEndDates &&
      value === FEB_28_29_MONTH_VALUE &&
      this.selectedYearValue
    ) {
      if (moment(this.selectedYearValue + '-02-29').isValid()) {
        return label.replace(/(^|\b)28\D/, '');
      } else {
        return label.replace(/\D29(\b|$)/, '');
      }
    }
    return label;
  }

  registerOnChange(fn: (value: string | null) => void) {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this._onTouched = fn;
  }

  writeValue(date: string): void {
    const dateAsMoment = moment(toUniversalDateString(date) || '').locale(this.locale);
    if (dateAsMoment.isValid()) {
      this.selectedYearValue = dateAsMoment.format('YYYY');
      this.selectedMonthValue = dateAsMoment.format('MM-DD');
      if (this.includesFebruaryEndDates && isEndOfFebruaryMonthDate(this.selectedMonthValue)) {
        this.selectedMonthValue = FEB_28_29_MONTH_VALUE;
      }
    } else {
      this.selectedYearValue = '';
      this.selectedMonthValue = '';
    }
  }

  onTouched(index: 0 | 1) {
    this._touched[index] = true;
    if (this._touched.every(x => x) || (this.selectedMonthValue && this.selectedYearValue)) {
      this._onTouched?.();
    }
  }

  onMonthChanged(month: string) {
    this.selectedMonthValue = month;
    this.onValueChanged();
  }

  onYearChanged(year: string) {
    this.selectedYearValue = year;
    this.onValueChanged();
  }

  onValueChanged() {
    if (
      this.selectedMonthValue &&
      this.selectedYearValue &&
      /^\d{4}$/.test(this.selectedYearValue)
    ) {
      if (this.includesFebruaryEndDates && this.selectedMonthValue === FEB_28_29_MONTH_VALUE) {
        if (moment(this.selectedYearValue + '-02-29').isValid()) {
          this._onChange?.(this.selectedYearValue + '-02-29');
        } else {
          this._onChange?.(this.selectedYearValue + '-02-28');
        }
      } else {
        this._onChange?.(this.selectedYearValue + '-' + this.selectedMonthValue);
      }
    } else {
      this._onChange?.(null);
    }
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }
}
